/**
 * PageGuide — the explainer that sits at the top of every Budget page.
 *
 * Two things, deliberately kept apart:
 *
 *   `children`  STATIC. What this page is and how to read it. Dismissible, and the dismissal
 *               is remembered, because someone who has used the page fifty times does not need
 *               to be told what it is a fifty-first time.
 *
 *   `signal`    LIVE. One sentence computed from the data actually on screen — "2 of 7 budgets
 *               are projected to overrun before period end." This is NOT dismissible. It is
 *               data, not chrome, and it is the reason the component earns its space: a page
 *               that shows you numbers and a page that TELLS you what they mean are different
 *               products.
 *
 * When there is nothing wrong, say so plainly ("Nothing needs your attention") rather than
 * showing an empty box. Silence reads as a bug; "all clear" reads as an answer.
 */

import { useCallback, useState } from 'react';
import { IconInfoCircle, IconChevronRight } from '@tabler/icons-react';
import { Alert, Button, Group, Stack, Text } from '@/components/ui';
import { Box } from '@mantine/core';

export type PageGuideTone = 'info' | 'success' | 'warning' | 'error';

export interface PageGuideProps {
  /**
   * Stable id for the dismissal memory. Omit to make the guide permanent (used on pages where
   * the explanation is the point, e.g. the anomaly queue).
   */
  storageKey?: string;
  title?: string;
  /** The static explanation. */
  children?: React.ReactNode;
  /** The live, data-derived sentence. Never hidden, even when the static help is dismissed. */
  signal?: React.ReactNode;
  /** Tone of the SIGNAL — drives the colour. Defaults to info. */
  tone?: PageGuideTone;
  /** Optional jump to whatever the signal just named. */
  action?: { label: string; onClick: () => void };
}

const STORAGE_PREFIX = 'ccms.budgets.guide.';

function readDismissed(key?: string): boolean {
  if (!key) return false;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key) === '1';
  } catch {
    // Private mode / storage disabled — show the guide rather than crashing over a nicety.
    return false;
  }
}

export function PageGuide({
  storageKey,
  title = 'About this page',
  children,
  signal,
  tone = 'info',
  action,
}: PageGuideProps) {
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (!storageKey) return;
    try {
      window.localStorage.setItem(STORAGE_PREFIX + storageKey, '1');
    } catch {
      /* nothing to do — the dismissal just won't persist */
    }
  }, [storageKey]);

  const showStatic = !!children && !dismissed;
  if (!showStatic && !signal) return null;

  return (
    <Alert
      type={tone}
      title={showStatic ? title : undefined}
      icon={<IconInfoCircle size={20} />}
      withCloseButton={showStatic && !!storageKey}
      onClose={dismiss}
    >
      <Stack gap={showStatic && signal ? 8 : 0}>
        {showStatic && (
          <Text size="sm" style={{ lineHeight: 1.55 }}>
            {children}
          </Text>
        )}

        {signal && (
          <Group gap="sm" wrap="wrap" align="center">
            <Box style={{ flex: 1, minWidth: 240 }}>
              <Text size="sm" fw={600} style={{ lineHeight: 1.5 }}>
                {signal}
              </Text>
            </Box>
            {action && (
              <Button
                size="xs"
                variant="light"
                rightSection={<IconChevronRight size={14} />}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </Alert>
  );
}

export default PageGuide;
